import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Building2, MapPin, Image as ImageIcon, Globe, Trash2 } from "lucide-react";

export default function Properties() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [props, setProps] = useState(null);

  const load = () => api.get("/properties").then(({ data }) => setProps(data)).catch(() => setProps([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const { data } = await api.post("/properties", { data: { name: "Nuovo Immobile" }, standard_rules_enabled: [], custom_rules: [] });
      navigate(`/properties/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Errore nella creazione"); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Eliminare questo immobile? Sarà archiviato anche su HolidayWorld.")) return;
    await api.delete(`/properties/${id}`);
    toast.success("Immobile eliminato");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">I Miei Immobili</h1>
          <p className="text-muted-foreground mt-1 text-sm">Crea e gestisci le schede delle tue strutture</p>
        </div>
        {user?.role !== "delegate" && (
          <button onClick={create} className="bg-primary text-primary-foreground font-medium px-4 py-2.5 rounded-lg shadow-sm hover:opacity-95 active:scale-[0.98] transition-all flex items-center gap-2" data-testid="property-add-new-button">
            <Plus className="h-4 w-4" /> Aggiungi Immobile
          </button>
        )}
      </div>

      {props === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{[1, 2, 3].map((i) => <div key={i} className="h-64 rounded-xl bg-muted animate-pulse" />)}</div>
      ) : props.length === 0 ? (
        <div className="bg-card rounded-xl border border-dashed border-border py-16 text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
          <h3 className="font-display text-lg font-semibold">Nessun immobile</h3>
          <p className="text-muted-foreground text-sm mt-1 mb-5">Inizia creando la tua prima scheda immobile.</p>
          {user?.role !== "delegate" && <button onClick={create} className="bg-primary text-primary-foreground font-medium px-5 py-2.5 rounded-lg inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Crea il primo immobile</button>}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {props.map((p) => (
            <div key={p.id} onClick={() => navigate(`/properties/${p.id}`)} className="bg-card rounded-xl border border-border/80 overflow-hidden hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(0,0,0,0.08)] hover:border-primary/40 cursor-pointer transition-all group" data-testid="property-card-item">
              <div className="aspect-[16/10] bg-muted relative overflow-hidden">
                {p.cover_url ? <img src={fileUrl(p.cover_url)} alt={p.data?.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  : <div className="h-full w-full grid place-items-center text-muted-foreground/40"><ImageIcon className="h-10 w-10" /></div>}
                {p.enabled_on_hw && <span className="absolute top-2 right-2 bg-emerald-700/90 text-white text-[11px] px-2 py-0.5 rounded-md font-semibold backdrop-blur-sm flex items-center gap-1"><Globe className="h-3 w-3" /> HolidayWorld</span>}
              </div>
              <div className="p-4">
                <h3 className="font-display font-semibold truncate">{p.data?.name || "Senza nome"}</h3>
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1 truncate"><MapPin className="h-3 w-3 shrink-0" /> {[p.data?.city, p.data?.province].filter(Boolean).join(", ") || "Indirizzo non impostato"}</p>
                <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                  <span>{p.data?.type || "—"} · {p.photo_count || 0} foto</span>
                  {user?.role !== "delegate" && <button onClick={(e) => remove(p.id, e)} className="text-muted-foreground/50 hover:text-destructive transition-colors p-1" data-testid="property-delete"><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
